import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { wallboardApi } from '../../../api/client';
import TenantPicker, { useTenantFilter } from './TenantPicker';
import HelpButton from '../../help/HelpButton';

const REFRESH_MS = 5_000;

const STATE_COLORS = {
  ready:            '#3BD162',
  aux:              '#F5A623',
  wrap:             '#2196F3',
  on_call:          '#9C27B0',
  // Agent-state hardening (2026-07-09): distinct color for agent-initiated outbound.
  on_call_outbound: '#14B8A6',
  logout:           '#9E9E9E',
};

const STATE_LABELS = {
  ready: 'Ready', aux: 'AUX', wrap: 'Wrap',
  on_call: 'On Call ↙', on_call_outbound: 'On Outbound ↗',
  logout: 'Not Ready',
};

function formatSecs(s) {
  if (!s || s < 0) return '0s';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${sec}s`;
}

function KPICard({ label, value, sub, accent, alert }) {
  return (
    <div
      className={
        'rounded-thin border bg-surface-panel dark:bg-surface-dark-panel p-4 transition-colors ' +
        (alert
          ? 'border-accent-red shadow-thin animate-pulse'
          : 'border-surface-border dark:border-surface-dark-border shadow-thin')
      }
    >
      <div className="text-xs uppercase tracking-wider text-surface-muted dark:text-surface-dark-muted">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-3xl font-semibold" style={{ color: accent || undefined }}>{value}</span>
        {sub && <span className="text-xs text-surface-muted">{sub}</span>}
      </div>
    </div>
  );
}

// Custom labels for the pie that render OUTSIDE the slice so digits don't get
// clipped against the chart edge.
function renderPieLabel({
  cx, cy, midAngle, outerRadius, value, name, percent,
}) {
  const RAD = Math.PI / 180;
  const r = outerRadius + 14;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return (
    <text
      x={x}
      y={y}
      fill="currentColor"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      style={{ fontSize: 12, fontWeight: 600 }}
    >
      {`${name} ${value} (${(percent * 100).toFixed(0)}%)`}
    </text>
  );
}

export default function Wallboard() {
  const [tvMode, setTvMode] = useState(false);
  const [filter, setFilter] = useState({ queue: '', did: '' });
  const [clientId, setClientId] = useTenantFilter();

  const { data, isLoading, error } = useQuery({
    queryKey: ['wallboard', 'floor', clientId || ''],
    queryFn: () => wallboardApi.floor(clientId || undefined),
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  // Listen for fullscreen-exit so the TV mode flag clears
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement) setTvMode(false); };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleTv = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      setTvMode(true);
    } else {
      document.exitFullscreen?.();
      setTvMode(false);
    }
  };

  if (error) {
    return <div className="p-6 text-sm text-accent-red">Failed to load: {error.message}</div>;
  }

  const totalCallers = data?.total_callers_waiting ?? 0;
  const totalOnHold  = data?.total_calls_on_hold ?? 0;
  const totalHandledToday   = data?.total_calls_handled_today ?? 0;
  const totalAbandonedToday = data?.total_calls_abandoned_today ?? 0;
  const longestWait  = data?.longest_wait ?? 0;
  const slaOverall   = data?.sla_overall ?? 0;
  const answerRateOverall  = data?.answer_rate_overall ?? 0;
  const abandonRateOverall = data?.abandon_rate_overall ?? 0;
  const totalOfferedToday  = data?.total_calls_offered_today ?? 0;
  // Per-tenant SL goal for pass/fail coloring (from the first queue's config;
  // all queues in a tenant share it). Default 80% when unknown.
  const slTargetPct  = (data?.queues || []).find(q => q.sl_target_pct)?.sl_target_pct ?? 80;
  const slThreshold  = (data?.queues || []).find(q => q.sl_threshold_sec)?.sl_threshold_sec ?? 20;
  const counts       = data?.agent_status_counts ?? { ready: 0, aux: 0, wrap: 0, on_call: 0, logout: 0 };

  // Exclude `logout` (Not Ready = not logged into the queue) from the pie —
  // per Fernando 2026-08-12 it adds no value on the status breakdown.
  const pieData = Object.entries(counts)
    .filter(([k, v]) => v > 0 && k !== 'logout')
    .map(([k, v]) => ({ name: STATE_LABELS[k] || k, value: v, fill: STATE_COLORS[k], stateKey: k }));

  const totalAgents = Object.values(counts).reduce((a, b) => a + b, 0);
  const availableNow = (counts.ready || 0);
  // Occupancy includes BOTH on_call directions + wrap-up.
  const occupied = (counts.on_call || 0) + (counts.on_call_outbound || 0) + (counts.wrap || 0);
  const occupancy = totalAgents > 0
    ? Math.round((occupied / totalAgents) * 100)
    : 0;

  // Per-queue chart: currently waiting + on-hold + handled + abandoned today
  const queueChart = (data?.queues || [])
    .map((q) => ({
      name: q.name || q.queue_id,
      waiting: q.callers || 0,
      onHold: q.calls_on_hold || 0,
      handled: q.calls_handled_today || 0,
      abandoned: q.calls_abandoned_today || 0,
    }))
    .slice(0, 12);

  return (
    <div className={`p-6 ${tvMode ? 'min-h-screen bg-surface-base dark:bg-surface-dark-base' : ''}`}>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className={`font-semibold ${tvMode ? 'text-3xl' : 'text-xl'}`}>Wallboard</h2>
          <p className="text-xs text-surface-muted">
            Auto-refresh every {REFRESH_MS / 1000}s
            {data?.captured_at && (
              <> · last update {new Date(data.captured_at).toLocaleTimeString()}</>
            )}
            {' '}· site <strong>{data?.site || '—'}</strong>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <TenantPicker value={clientId} onChange={setClientId} />
          <HelpButton moduleKey="acd_floor" />
          <button
            onClick={toggleTv}
            className="px-3 py-1.5 rounded-thin text-sm border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
          >
            {tvMode ? '⇲ Exit Full Screen' : '⛶ Full Screen'}
          </button>
        </div>
      </div>

      {isLoading && !data && (
        <div className="text-sm text-surface-muted">Loading…</div>
      )}

      {/* KPI cards — row 1: live ops (the 4 client-requested realtime metrics) */}
      <div className={`grid gap-3 mb-3 grid-cols-2 md:grid-cols-4`}>
        <KPICard
          label="Calls in Queue"
          value={totalCallers}
          sub="Waiting to be answered"
          alert={totalCallers > 0}
          accent={totalCallers > 0 ? '#E53935' : undefined}
        />
        <KPICard
          label="Calls on Hold"
          value={totalOnHold}
          sub="Currently on MOH"
          alert={totalOnHold > 0}
          accent={totalOnHold > 0 ? '#F5A623' : undefined}
        />
        <KPICard
          label="Calls Handled"
          value={totalHandledToday}
          sub="Today so far (tenant TZ)"
        />
        <KPICard
          label="Calls Abandoned"
          value={totalAbandonedToday}
          sub="Today so far (tenant TZ)"
          accent={totalAbandonedToday > 0 ? '#F5A623' : undefined}
        />
      </div>

      {/* KPI cards — row 2: secondary live-ops indicators */}
      <div className="grid gap-3 mb-4 grid-cols-2 md:grid-cols-5">
        <KPICard
          label="Longest wait"
          value={formatSecs(longestWait)}
          alert={longestWait > 30}
          accent={longestWait > 30 ? '#E53935' : undefined}
        />
        <KPICard
          label={`Service Level (${slThreshold}s)`}
          value={totalOfferedToday > 0 ? `${(slaOverall * 100).toFixed(0)}%` : '—'}
          sub={totalOfferedToday > 0
            ? `goal ${slTargetPct}% · ${totalOfferedToday} offered today`
            : 'no calls offered today'}
          accent={totalOfferedToday > 0
            ? (slaOverall * 100 >= slTargetPct ? '#3BD162' : '#E53935')
            : undefined}
        />
        <KPICard
          label="Answer rate"
          value={totalOfferedToday > 0 ? `${(answerRateOverall * 100).toFixed(0)}%` : '—'}
          sub={totalOfferedToday > 0
            ? `${totalHandledToday} ans / ${totalAbandonedToday} aband`
            : 'no calls offered today'}
          accent={totalOfferedToday > 0
            ? (abandonRateOverall > 0.1 ? '#F5A623' : '#3BD162')
            : undefined}
        />
        <KPICard
          label="Agents available"
          value={`${availableNow}`}
          sub={`${counts.on_call || 0} in / ${counts.on_call_outbound || 0} out / ${counts.aux || 0} AUX`}
          accent={availableNow > 0 ? '#3BD162' : undefined}
        />
        {/* Occupancy % removed 2026-08-12 (Jeff): misleading vs. the report-side
            "how busy are they" occupancy; dropped from the live wallboard. */}
      </div>

      {/* Charts row: pie + per-queue bar chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Pie */}
        <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel p-4 shadow-thin">
          <h3 className="text-sm font-semibold mb-2">Agent status</h3>
          {pieData.length === 0 ? (
            <div className="text-xs text-surface-muted py-8 text-center">No agents in any portal-managed queue.</div>
          ) : (
            <div style={{ height: tvMode ? 360 : 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 16, right: 80, bottom: 16, left: 80 }}>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={tvMode ? 100 : 75}
                    label={renderPieLabel}
                    labelLine
                    isAnimationActive={false}
                  >
                    {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} />
                  <Legend verticalAlign="bottom" height={24} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Per-queue bar chart */}
        <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel p-4 shadow-thin">
          <h3 className="text-sm font-semibold mb-2">Per-queue today</h3>
          {queueChart.length === 0 ? (
            <div className="text-xs text-surface-muted py-8 text-center">No data yet.</div>
          ) : (
            <div style={{ height: tvMode ? 360 : 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={queueChart} margin={{ top: 10, right: 16, bottom: 24, left: 0 }}>
                  <CartesianGrid stroke="rgba(120,120,120,0.15)" strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} dy={6} height={48} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="waiting"   stackId="a" fill="#E53935" name="In Queue" />
                  <Bar dataKey="onHold"    stackId="a" fill="#F59E0B" name="On Hold" />
                  <Bar dataKey="handled"   stackId="a" fill="#3BD162" name="Handled today" />
                  <Bar dataKey="abandoned" stackId="a" fill="#F5A623" name="Abandoned today" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Per-queue table */}
      <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel p-4 shadow-thin">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h3 className="text-sm font-semibold">Per-queue summary</h3>
          <div className="flex items-center gap-2 text-xs">
            <select
              value={filter.queue}
              onChange={(e) => setFilter((f) => ({ ...f, queue: e.target.value }))}
              className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin"
            >
              <option value="">All queues</option>
              {(data?.queues || []).map((q) => (
                <option key={q.queue_id} value={q.queue_id}>{q.queue_id} · {q.name}</option>
              ))}
            </select>
            <input
              type="search"
              placeholder="DID / DN"
              value={filter.did}
              onChange={(e) => setFilter((f) => ({ ...f, did: e.target.value }))}
              className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin w-32"
            />
            {(filter.queue || filter.did) && (
              <button
                onClick={() => setFilter({ queue: '', did: '' })}
                className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-surface-muted dark:text-surface-dark-muted">
            <tr>
              <th className="px-2 py-1">Queue</th>
              <th className="px-2 py-1 text-right" title="Currently waiting to be answered">In Queue</th>
              <th className="px-2 py-1 text-right" title="Currently on hold (MOH)">On Hold</th>
              <th className="px-2 py-1 text-right" title="Offered today = answered + abandoned (tenant TZ)">Offered</th>
              <th className="px-2 py-1 text-right" title="Answered today (tenant TZ, resets at local midnight)">Handled</th>
              <th className="px-2 py-1 text-right" title="Abandoned today (tenant TZ)">Abandoned</th>
              <th className="px-2 py-1 text-right" title="Answer rate = answered / offered">Ans %</th>
              <th className="px-2 py-1 text-right">Longest</th>
              <th className="px-2 py-1 text-right">Members</th>
              <th className="px-2 py-1 text-right">Avail</th>
              <th className="px-2 py-1 text-right" title="Service Level = answered within threshold / offered">SL</th>
              <th className="px-2 py-1">Auto</th>
            </tr>
          </thead>
          <tbody>
            {(data?.queues || []).length === 0 && (
              <tr><td colSpan={12} className="text-center text-xs text-surface-muted py-4">No portal-managed queues for the current filter.</td></tr>
            )}
            {(data?.queues || [])
              .filter((q) => !filter.queue || q.queue_id === filter.queue)
              .filter((q) => !filter.did || (q.did || '').includes(filter.did))
              .map((q) => (
              <tr key={q.queue_id} className="border-t border-surface-border dark:border-surface-dark-border">
                <td className="px-2 py-1.5">
                  <span className="font-mono text-xs mr-2">{q.queue_id}</span>
                  {q.name}
                </td>
                <td className={`px-2 py-1.5 text-right font-mono ${q.callers > 0 ? 'text-accent-red font-semibold' : ''}`}>{q.callers}</td>
                <td className={`px-2 py-1.5 text-right font-mono ${(q.calls_on_hold || 0) > 0 ? 'text-accent-orange font-semibold' : ''}`}>{q.calls_on_hold ?? 0}</td>
                <td className="px-2 py-1.5 text-right font-mono">{q.offered_today ?? 0}</td>
                <td className="px-2 py-1.5 text-right font-mono">{q.calls_handled_today ?? 0}</td>
                <td className={`px-2 py-1.5 text-right font-mono ${(q.calls_abandoned_today ?? 0) > 0 ? 'text-accent-amber font-semibold' : ''}`}>{q.calls_abandoned_today ?? 0}</td>
                <td className="px-2 py-1.5 text-right font-mono">{(q.offered_today ?? 0) > 0 ? `${((q.answer_rate ?? 0) * 100).toFixed(0)}%` : '—'}</td>
                <td className={`px-2 py-1.5 text-right font-mono ${q.longest_wait > 30 ? 'text-accent-red font-semibold' : ''}`}>{formatSecs(q.longest_wait)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{q.members}</td>
                <td className="px-2 py-1.5 text-right font-mono">{q.members_available}</td>
                <td className={`px-2 py-1.5 text-right font-mono ${(q.offered_today ?? 0) > 0 ? ((q.sla * 100) >= (q.sl_target_pct ?? 80) ? 'text-accent-green font-semibold' : 'text-accent-red font-semibold') : ''}`} title={(q.offered_today ?? 0) > 0 ? `${q.sl_threshold_sec ?? 20}s threshold, goal ${q.sl_target_pct ?? 80}%` : ''}>{(q.offered_today ?? 0) > 0 ? `${(q.sla * 100).toFixed(0)}%` : '—'}</td>
                <td className="px-2 py-1.5">{q.auto_answer ? <span title="auto-answer">✨</span> : <span className="opacity-30">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
